import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AnalyticsSection({ user }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState(false);

  useEffect(() => {
    checkPremiumAndFetchData();
  }, []);

  const checkPremiumAndFetchData = async () => {
    try {
      // Check premium status first
      const statusResponse = await axios.get(`${API}/users/${user.id}/premium/status`);
      setIsPremium(statusResponse.data.is_premium);

      if (statusResponse.data.is_premium) {
        // Fetch analytics data
        const analyticsResponse = await axios.get(`${API}/users/${user.id}/analytics`);
        setAnalyticsData(analyticsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateTrial = async () => {
    setActivatingTrial(true);
    try {
      const response = await axios.post(`${API}/users/${user.id}/premium/activate-trial`);
      if (response.data.status === 'trial_activated') {
        alert('🎉 Premium trial activated! Enjoy all premium features for 7 days.');
        setShowTrialModal(false);
        checkPremiumAndFetchData();
      } else if (response.data.status === 'already_premium') {
        alert('You already have premium access!');
        setShowTrialModal(false);
      } else if (response.data.status === 'trial_used') {
        alert('You have already used your free trial.');
        setShowTrialModal(false);
      }
    } catch (error) {
      console.error('Error activating trial:', error);
      alert('Failed to activate trial. Please try again.');
    } finally {
      setActivatingTrial(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl accent-text">Loading analytics...</div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="max-w-4xl mx-auto">
        {showTrialModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
            <div className="theme-card rounded-2xl border-2 p-8 max-w-md w-full" style={{ borderColor: 'var(--accent-from)' }}>
              <div className="text-center">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-2xl font-bold accent-text mb-2">Premium Feature</h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Analytics Dashboard is a premium feature. Start your <span className="accent-text font-semibold">7-day free trial</span> to unlock detailed insights!
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleActivateTrial}
                    disabled={activatingTrial}
                    className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-pink-600 transition disabled:opacity-50"
                  >
                    {activatingTrial ? 'Activating...' : '🎉 Start Free Trial'}
                  </button>
                  <button
                    onClick={() => setShowTrialModal(false)}
                    className="w-full py-2 transition"
                    style={{ color: 'var(--text-muted)' }}
                    onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                    onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="theme-card rounded-2xl border p-12 text-center">
          <div className="text-6xl mb-6">📊🔒</div>
          <h2 className="text-3xl font-bold accent-text mb-4">Analytics Dashboard</h2>
          <p className="mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-muted)' }}>
            Unlock powerful insights into your content performance, engagement trends, and audience behavior with our premium analytics dashboard.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📈</div>
              <div className="font-semibold accent-text">Performance Tracking</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Monitor engagement rates</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📊</div>
              <div className="font-semibold accent-text">Content Analysis</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Top performing posts</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">🎯</div>
              <div className="font-semibold accent-text">Platform Insights</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Best platforms for you</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📅</div>
              <div className="font-semibold accent-text">Trend Analysis</div>
              <div className="text-sm" style={{ color: 'var(--text-muted)' }}>7-day performance trends</div>
            </div>
          </div>

          <button
            onClick={() => setShowTrialModal(true)}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-bold text-lg hover:from-orange-600 hover:to-pink-600 transition"
          >
            🚀 Unlock Analytics Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold accent-text">📊 Analytics Dashboard</h2>
          <p className="mt-1" style={{ color: 'var(--text-muted)' }}>Track your content performance and insights</p>
        </div>
        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/50 rounded-lg px-4 py-2">
          <div className="text-green-400 font-semibold text-sm">⭐ Premium Active</div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="theme-card-nested border-2 rounded-xl p-6" style={{ borderColor: 'var(--accent-from)', backgroundColor: 'rgba(var(--accent-from-rgb, 249, 115, 22), 0.1)' }}>
          <div className="text-3xl mb-2">📝</div>
          <div className="text-3xl font-bold accent-text mb-1">{analyticsData?.total_posts || 0}</div>
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Posts</div>
        </div>

        <div className="theme-card-nested border-2 rounded-xl p-6" style={{ borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
          <div className="text-3xl mb-2">📊</div>
          <div className="text-3xl font-bold text-blue-400 mb-1">{analyticsData?.posts_this_month || 0}</div>
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Posts This Month</div>
        </div>

        <div className="theme-card-nested border-2 rounded-xl p-6" style={{ borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
          <div className="text-3xl mb-2">📈</div>
          <div className="text-3xl font-bold text-green-400 mb-1">{analyticsData?.avg_engagement_rate || 0}%</div>
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Avg Engagement</div>
        </div>

        <div className="theme-card-nested border-2 rounded-xl p-6" style={{ borderColor: '#ec4899', backgroundColor: 'rgba(236, 72, 153, 0.1)' }}>
          <div className="text-3xl mb-2">🎯</div>
          <div className="text-3xl font-bold text-pink-400 mb-1">{analyticsData?.best_platform || 'N/A'}</div>
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Best Platform</div>
        </div>
      </div>

      {/* Engagement Trend Chart */}
      <div className="theme-card border rounded-xl p-6">
        <h3 className="text-xl font-bold accent-text mb-4">📈 7-Day Engagement Trend</h3>
        <div className="space-y-3">
          {analyticsData?.engagement_trend?.map((day, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="text-sm w-24" style={{ color: 'var(--text-muted)' }}>{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              <div className="flex-1 rounded-full h-8 relative overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                <div 
                  className="accent-gradient h-full rounded-full transition-all duration-500"
                  style={{ width: `${(day.engagement / 6) * 100}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {day.engagement}% engagement
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Performance */}
      <div className="theme-card border rounded-xl p-6">
        <h3 className="text-xl font-bold accent-text mb-4">📱 Platform Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(analyticsData?.platform_performance || {}).map(([platform, data]) => (
            <div key={platform} className="theme-card-nested border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-bold accent-text">{platform}</h4>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{data.posts} posts</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold accent-text">{data.avg_engagement}%</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Avg Engagement</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Breakdown */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-bold accent-text mb-4">🎨 Content Type Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analyticsData?.content_breakdown || {}).map(([type, count]) => (
            <div key={type} className="theme-card-nested border rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-orange-400 mb-1">{count}%</div>
              <div className="text-sm style={{ color: 'var(--text-muted)' }}">{type}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performing Content */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6">
        <h3 className="text-xl font-bold accent-text mb-4">🏆 Top Performing Content</h3>
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🎯</div>
          <div className="text-2xl font-bold accent-text mb-2">{analyticsData?.top_performing_content}</div>
          <p className="style={{ color: 'var(--text-muted)' }}">Your most engaging content type</p>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="text-4xl">💡</div>
          <div>
            <h3 className="text-xl font-bold accent-text mb-2">AI Insights</h3>
            <p className="text-gray-300">
              Your <strong className="text-orange-400">{analyticsData?.top_performing_content}</strong> content performs best on{' '}
              <strong className="text-orange-400">{analyticsData?.best_platform}</strong>. 
              Keep creating diverse, engaging content to maintain your {analyticsData?.avg_engagement_rate}% engagement rate!
            </p>
          </div>
        </div>
      </div>

      {/* Old insights section removed - replaced with data-driven insights above */}
      <div className="hidden">
        <div className="p-3 bg-gray-800 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">Behind The Scenes</span>
                <span className="text-green-400 font-bold">+29%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsSection;
